/**
 * NameWithAnimals - Explore the letters and animals in your name
 * User records or types their name, then sees it spelled with animal letters and hears it read
 */

import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mic2, MicOff } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { animalAlphabetData } from '../../data/animalAlphabetGrid';
import {
  speakSequence,
  playClickFeedback,
} from '../../lib/naturalVoiceService';

// Shared design tokens with the rest of the AnimalABCs game family.
const PASTEL_COLORS = [
  '#dbeef5',
  '#fce8d2',
  '#dcecda',
  '#fadcdc',
  '#e8defc',
  '#d6f0e0',
];
const INK_GREEN = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

interface NameWithAnimalsProps {
  onGoAlphabet?: () => void;
  onGoCounting?: () => void;
}

type Phase = 'record' | 'transcribing' | 'result' | 'error';

export function NameWithAnimals({ onGoAlphabet, onGoCounting }: NameWithAnimalsProps) {
  const { language } = useLanguage();
  const [phase, setPhase] = useState<Phase>('record');
  const [isRecording, setIsRecording] = useState(false);
  const [name, setName] = useState('');
  const [typedName, setTypedName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const t = {
    en: {
      title: 'Letters & Animals in Your Name',
      subtitle: 'Say or type your name to see it with animal letters',
      prompt: 'Say your name, then tap the mic to stop',
      orType: 'Or type your name',
      record: 'Record',
      stop: 'Stop',
      recording: 'Recording…',
      transcribing: 'Understanding your name…',
      yourName: 'Your name is',
      tryAgain: 'Try again',
      goCta: 'Go',
      listenAgain: 'Listen again',
      placeholder: 'Type your name here',
      micPermission: 'Please allow microphone access to record.',
      errorTranscribe: 'Could not understand. Try again or type your name.',
      errorModerated: "Let's try your name again. Say or type only your name.",
      errorTooLong: 'Names are usually shorter. Try your first name or a nickname.',
      alphabet: 'Alphabet',
      counting: 'Counting',
    },
    es: {
      title: 'Letras y Animales en Tu Nombre',
      subtitle: 'Di o escribe tu nombre para verlo con letras de animales',
      prompt: 'Di tu nombre, luego toca el micrófono para detener',
      orType: 'O escribe tu nombre',
      record: 'Grabar',
      stop: 'Detener',
      recording: 'Grabando…',
      transcribing: 'Entendiendo tu nombre…',
      yourName: 'Tu nombre es',
      tryAgain: 'Intentar de nuevo',
      goCta: 'Ir',
      listenAgain: 'Escuchar de nuevo',
      placeholder: 'Escribe tu nombre aquí',
      micPermission: 'Por favor permite el acceso al micrófono para grabar.',
      errorTranscribe: 'No pude entender. Intenta de nuevo o escribe tu nombre.',
      errorModerated: 'Intentemos de nuevo. Di o escribe solo tu nombre.',
      errorTooLong: 'Los nombres suelen ser más cortos. Prueba tu primer nombre o un apodo.',
      alphabet: 'Alfabeto',
      counting: 'Contar',
    },
  }[language];

  const MAX_NAME_LENGTH = 50;
  const NAME_REGEX = /^[\p{L}\p{M}\s\-']+$/u;

  const letterToAnimal = new Map(
    animalAlphabetData.map((d) => [
      d.letter.toUpperCase(),
      { animal: language === 'en' ? d.animalEn : d.animalEs, emoji: language === 'en' ? d.emojiEn : d.emojiEs },
    ])
  );

  const extractLetters = (str: string): string[] => {
    const accentMap: Record<string, string> = {
      Á: 'A', À: 'A', Â: 'A', Ä: 'A', É: 'E', È: 'E', Ê: 'E', Ë: 'E',
      Í: 'I', Ì: 'I', Î: 'I', Ï: 'I', Ó: 'O', Ò: 'O', Ô: 'O', Ö: 'O',
      Ú: 'U', Ù: 'U', Û: 'U', Ü: 'U', Ñ: 'N', Ç: 'C',
    };
    return str
      .toUpperCase()
      .split('')
      .map((c) => accentMap[c] || c)
      .filter((c) => /[A-Z]/.test(c));
  };

  const validateNameFormat = (s: string): string | null => {
    const trimmed = s.trim();
    if (trimmed.length === 0) return null;
    if (trimmed.length > MAX_NAME_LENGTH) return 'tooLong';
    if (!NAME_REGEX.test(trimmed)) return null;
    const letters = extractLetters(trimmed);
    if (letters.length === 0) return null;
    return trimmed;
  };

  const transcribeAudio = async (blob: Blob): Promise<{ text: string } | { moderated: true } | null> => {
    const arrayBuffer = await blob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce((s, b) => s + String.fromCharCode(b), '')
    );
    const res = await fetch('/api/speech-to-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audio: base64, language }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.details || `API error ${res.status}`);
    }
    const data = await res.json();
    if (data.moderated) return { moderated: true };
    const text = (data.text || '').trim();
    return text ? { text } : null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setPhase('transcribing');
        setErrorMessage('');
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const result = await transcribeAudio(blob);
          if (result && 'moderated' in result) {
            setErrorMessage(t.errorModerated);
            setPhase('error');
            return;
          }
          if (result && 'text' in result) {
            const clean = result.text.replace(/[^a-zA-ZáéíóúñÁÉÍÓÚÑ\s\-']/g, '').trim();
            const validated = validateNameFormat(clean);
            if (validated === 'tooLong') {
              setErrorMessage(t.errorTooLong);
              setPhase('error');
              return;
            }
            if (validated) {
              setName(validated);
              setPhase('result');
              setTimeout(() => readNameAloud(validated), 600);
              return;
            }
          }
          setErrorMessage(t.errorTranscribe);
          setPhase('error');
        } catch {
          setErrorMessage(t.errorTranscribe);
          setPhase('error');
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      playClickFeedback();
    } catch {
      setErrorMessage(t.micPermission);
      setPhase('error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleSubmitTyped = async () => {
    const validated = validateNameFormat(typedName);
    if (validated === 'tooLong') {
      setErrorMessage(t.errorTooLong);
      setPhase('error');
      return;
    }
    if (!validated) return;
    setPhase('transcribing');
    setErrorMessage('');
    try {
      const res = await fetch('/api/moderate-name', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: validated }),
      });
      const data = await res.json();
      if (data.moderated || !data.ok) {
        setErrorMessage(t.errorModerated);
        setPhase('error');
        return;
      }
      const safeName = data.text || validated;
      setName(safeName);
      setPhase('result');
      playClickFeedback();
      setTimeout(() => readNameAloud(safeName), 600);
    } catch {
      setErrorMessage(t.errorTranscribe);
      setPhase('error');
    }
  };

  const readNameAloud = (nameStr: string) => {
    const letters = extractLetters(nameStr);
    if (letters.length === 0) return;

    const isEn = language === 'en';
    const intro = `${t.yourName} ${nameStr}.`;
    const phrases: string[] = [intro];

    letters.forEach((letter) => {
      const entry = letterToAnimal.get(letter);
      if (entry) {
        phrases.push(letter);
        phrases.push(isEn ? `is for ${entry.animal}` : `de ${entry.animal}`);
      }
    });

    speakSequence({ phrases, language: language as 'en' | 'es', pauseMs: 120 });
  };

  const reset = () => {
    setPhase('record');
    setName('');
    setTypedName('');
    setErrorMessage('');
  };

  const letters = extractLetters(name);
  const hasResult = phase === 'result' && letters.length > 0;

  // Shared pill styles
  const primaryPill: React.CSSProperties = {
    background: BRAND_GREEN,
    color: '#ffffff',
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 18,
    padding: '12px 24px',
    borderRadius: 999,
    border: 'none',
    cursor: 'pointer',
  };
  const outlinePill: React.CSSProperties = {
    background: 'transparent',
    color: BRAND_GREEN,
    border: `1.5px solid ${BRAND_GREEN}`,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 18,
    padding: '12px 24px',
    borderRadius: 999,
    cursor: 'pointer',
  };
  const cardSurface: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e8e0c8',
    borderRadius: 24,
  };

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: '#fbfbe8' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl mb-2"
            style={{
              fontFamily: SERIF,
              fontWeight: 300,
              color: INK_GREEN,
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl"
            style={{ fontFamily: SANS, fontWeight: 400, color: '#355c46' }}
          >
            {t.subtitle}
          </motion.p>
        </div>

        {/* Sibling navigation pills (replaces back button) */}
        {(onGoAlphabet || onGoCounting) && (
          <div className="flex justify-center gap-3 mb-6 flex-wrap">
            {onGoAlphabet && (
              <motion.button
                onClick={onGoAlphabet}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                style={outlinePill}
              >
                {t.alphabet}
              </motion.button>
            )}
            {onGoCounting && (
              <motion.button
                onClick={onGoCounting}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                style={outlinePill}
              >
                {t.counting}
              </motion.button>
            )}
          </div>
        )}

        {phase === 'record' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-8 md:p-10"
            style={cardSurface}
          >
            <p
              className="text-center mb-8"
              style={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: 'clamp(22px, 3vw, 30px)',
                fontWeight: 400,
                color: INK_GREEN,
              }}
            >
              {t.prompt}
            </p>
            <div className="flex justify-center mb-8">
              <motion.button
                onClick={isRecording ? stopRecording : startRecording}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="flex items-center gap-3"
                style={{
                  padding: '18px 36px',
                  borderRadius: 999,
                  fontFamily: SANS,
                  fontSize: 24,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  background: isRecording ? '#b85a55' : BRAND_GREEN,
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(66,165,122,0.25)',
                }}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-7 h-7" strokeWidth={1.6} />
                    {t.stop}
                  </>
                ) : (
                  <>
                    <Mic2 className="w-7 h-7" strokeWidth={1.6} />
                    {t.record}
                  </>
                )}
              </motion.button>
            </div>
            <div
              className="pt-6"
              style={{ borderTop: '1px solid #e8e0c8' }}
            >
              <p
                className="mb-3"
                style={{
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#9a9a9a',
                  textAlign: 'center',
                }}
              >
                {t.orType}
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitTyped()}
                  placeholder={t.placeholder}
                  maxLength={MAX_NAME_LENGTH}
                  className="flex-1 focus:outline-none"
                  style={{
                    padding: '12px 18px',
                    borderRadius: 14,
                    border: `1.5px solid ${INK_GREEN}33`,
                    fontFamily: SERIF,
                    fontSize: 22,
                    fontWeight: 400,
                    color: INK_GREEN,
                    background: '#fbfbe8',
                  }}
                />
                <button
                  onClick={handleSubmitTyped}
                  disabled={!typedName.trim()}
                  style={{
                    ...primaryPill,
                    padding: '0 28px',
                    opacity: typedName.trim() ? 1 : 0.4,
                    cursor: typedName.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  {t.goCta}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {(phase === 'transcribing' || phase === 'error') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-8 md:p-10 text-center"
            style={cardSurface}
          >
            {phase === 'transcribing' ? (
              <p
                className="animate-pulse"
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontSize: 28,
                  color: INK_GREEN,
                }}
              >
                {t.transcribing}
              </p>
            ) : (
              <>
                <p
                  className="mb-6"
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 22,
                    color: '#b85a55',
                  }}
                >
                  {errorMessage}
                </p>
                <button onClick={reset} style={primaryPill}>
                  {t.tryAgain}
                </button>
              </>
            )}
          </motion.div>
        )}

        {hasResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-8 md:p-10"
            style={cardSurface}
          >
            <p
              className="text-center mb-6"
              style={{
                fontFamily: SERIF,
                fontSize: 'clamp(28px, 4vw, 40px)',
                fontWeight: 400,
                color: INK_GREEN,
                letterSpacing: '-0.01em',
              }}
            >
              {t.yourName}{' '}
              <span style={{ fontWeight: 600, fontStyle: 'italic' }}>{name}</span>
            </p>

            {/* Letter cards — same pastel + serif treatment as Alphabet */}
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 my-8">
              {letters.map((letter, i) => {
                const entry = letterToAnimal.get(letter);
                if (!entry) return null;
                const pastel = PASTEL_COLORS[i % PASTEL_COLORS.length];
                return (
                  <motion.div
                    key={`${letter}-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e8e0c8',
                      borderRadius: 16,
                      padding: 8,
                      width: 100,
                    }}
                  >
                    <div
                      style={{
                        background: pastel,
                        borderRadius: 12,
                        padding: '14px 8px 10px',
                        textAlign: 'center',
                      }}
                    >
                      <span
                        className="block"
                        style={{
                          fontFamily: SERIF,
                          fontSize: 56,
                          fontWeight: 600,
                          color: INK_GREEN,
                          lineHeight: 1,
                        }}
                      >
                        {letter}
                      </span>
                      <span
                        className="block"
                        style={{
                          fontSize: 28,
                          lineHeight: 1,
                          marginTop: 8,
                          animation: 'float 3s ease-in-out infinite',
                        }}
                      >
                        {entry.emoji}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontSize: 13,
                        fontWeight: 500,
                        color: INK_GREEN,
                        textAlign: 'center',
                        marginTop: 8,
                      }}
                    >
                      {entry.animal}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={() => readNameAloud(name)} style={primaryPill}>
                {t.listenAgain}
              </button>
              <button onClick={reset} style={outlinePill}>
                {t.tryAgain}
              </button>
            </div>
          </motion.div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
